import { getRankedColorsForCount, COLOR_FLYWEIGHT, formatData } from '@utilities';

import { addSlideFooter } from '../slide_footer';
import { cleanQuestionTitle } from 'app/pages/Dashboard/pages/Survey/components/Dashboard/utilities/helpers.js';
import { FOOTER_TEXT, SURVEY_TITLE_OPTIONS, TEXT_COLOR, TABLE_BORDER } from '../constants';

/**
 * Gets rank chart data from question data
 * @param {Object} questionData
 * @param {String} displayAs
 * @returns {Object}
 */
const getRankChartData = (questionData, displayAs = 'pct') => {
  if (!questionData?.segments?.length) return null;
  const firstSegment = questionData.segments[0];
  const isCountDisplay = displayAs === 'count';
  const isPercentageDisplay = !isCountDisplay;

  const requiredChartData = isCountDisplay ? firstSegment?.chartCount : firstSegment?.chartPct;

  if (requiredChartData?.series && requiredChartData?.xAxis?.categories) {
    return processChartDataSimple(
      requiredChartData,
      requiredChartData.xAxis.categories,
      isPercentageDisplay,
      questionData
    );
  }

  if (firstSegment?.results?.length) {
    const categories = firstSegment.results.map((item) => item.title);
    const series = [];
    if (firstSegment.results[0]?.data) {
      const sampleData = firstSegment.results[0].data;

      const rankKeys = Object.keys(sampleData)
        .filter((key) => {
          if (key.match(/^\d{1,2}$/) && parseInt(key) <= 15) return true;
          if (key.match(/^rank_\d+$/)) return true;
          return false;
        })
        .sort((a, b) => {
          const aNum = parseInt(a.replace('rank_', ''));
          const bNum = parseInt(b.replace('rank_', ''));
          return aNum - bNum;
        });
      // Create series for each rank
      rankKeys.forEach((rankKey, index) => {
        const rankNumber = parseInt(rankKey.replace('rank_', '')) || index + 1;
        const rankData = firstSegment.results.map((item) => {
          const value = item.data?.[rankKey] || 0;
          return typeof value === 'number' ? value : parseFloat(value) || 0;
        });

        series.push({
          data: rankData,
          id: rankNumber.toString(),
          name:
            rankNumber === 1
              ? '1 (HIGH)'
              : rankNumber === rankKeys.length
                ? `${rankNumber} (LOW)`
                : rankNumber.toString(),
        });
      });

      const chartDataFromResults = {
        series,
        xAxis: { categories },
      };

      return processChartDataSimple(
        chartDataFromResults,
        categories,
        isPercentageDisplay,
        questionData
      );
    }
  }

  return null;
};

const processChartDataSimple = (chartData, categories, isPercentageDisplay, questionData) => {
  const reversedCategories = [...categories].reverse();
  if (chartData.series) {
    chartData.series.forEach((series) => {
      if (series.data) {
        series.data.forEach((data, index) => {
          if (data === 'NAN_SENTINEL') {
            series.data[index] = null;
          }
        });
      }
    });
  }

  const series = chartData.series;
  const validSeries = series.filter((seriesItem) => {
    const hasData = (seriesItem.data || []).some((d) => {
      if (d === null || d === undefined) return false;
      if (typeof d === 'object' && d.high !== undefined && d.low !== undefined) {
        return Math.abs(d.high - d.low) > 0;
      }
      return Math.abs(parseFloat(d) || 0) > 0;
    });
    return hasData;
  });
  const sortedSeries = [...validSeries].sort((a, b) => {
    const getRankNumber = (item) => {
      if (item.id && !isNaN(parseInt(item.id))) {
        return parseInt(item.id);
      }
      if (item.name) {
        const match = item.name.match(/(\d+)/);
        if (match) {
          return parseInt(match[1]);
        }
      }
      return 0;
    };

    return getRankNumber(a) - getRankNumber(b);
  });

  const dataSeries = sortedSeries.map((seriesItem, seriesIndex) => {
    const dataArray = (seriesItem.data || []).map((value, valueIndex) => {
      if (value === 'NAN_SENTINEL' || value === null || value === undefined) {
        return 0;
      }

      let numericValue;

      // Handle the high/low range format from convertChartToColumnRange
      if (typeof value === 'object' && value !== null && 'high' in value && 'low' in value) {
        const span = Math.abs(value.high - value.low);
        numericValue = span;
      } else if (typeof value === 'object' && value !== null) {
        if (value.y !== undefined) {
          numericValue = Math.abs(parseFloat(value.y));
        } else if (value.percentage !== undefined) {
          numericValue = Math.abs(parseFloat(value.percentage));
        } else if (value.count !== undefined) {
          numericValue = Math.abs(parseFloat(value.count));
        } else {
          const numericKeys = Object.keys(value).filter((key) => !isNaN(parseFloat(value[key])));
          if (numericKeys.length > 0) {
            numericValue = Math.abs(parseFloat(value[numericKeys[0]]));
          } else {
            numericValue = 0;
          }
        }
      } else {
        numericValue = Math.abs(parseFloat(value));
      }

      if (isNaN(numericValue)) {
        return 0;
      }

      if (isPercentageDisplay) {
        if (Math.abs(numericValue) > 2) {
          numericValue = numericValue / 100;
        }
      }

      return numericValue;
    });

    // Reverse the data array to match the reversed categories
    const reversedData = dataArray.reverse();

    const getRankNumber = (item) => {
      if (item.id && !isNaN(parseInt(item.id))) {
        return parseInt(item.id);
      }
      if (item.name) {
        const match = item.name.match(/(\d+)/);
        if (match) {
          return parseInt(match[1]);
        }
      }
      return seriesIndex + 1;
    };

    const rankNumber = getRankNumber(seriesItem);
    const totalRanks = sortedSeries.length;
    const seriesName = seriesItem.name || `${rankNumber}`;

    const midpoint = Math.ceil(totalRanks / 2);

    const divergingData = reversedData.map((value) => {
      // Lower ranks (5,6,7,8) should be negative (left side, red)
      // Higher ranks (1,2,3,4) should be positive (right side, green)
      if (rankNumber > midpoint) {
        return -Math.abs(value); // Ranks 5-8: negative values (left side)
      } else {
        return Math.abs(value); // Ranks 1-4: positive values (right side)
      }
    });

    return {
      color: null, // Will be set by color function
      labels: reversedCategories,
      name: seriesName,
      values: divergingData,
    };
  });

  const actualValidSeriesCount = validSeries.length;
  const allColors = getRankedColorsForCount(actualValidSeriesCount);

  const coloredDataSeries = dataSeries.map((series, index) => {
    const seriesName = series.name;
    // Extract rank number from series name (e.g., "1 (HIGH)" -> 1, "8 (LOW)" -> 8)
    const rankMatch = seriesName.match(/(\d+)/);
    const rankNumber = rankMatch ? parseInt(rankMatch[1]) : index + 1;

    const totalRanks = actualValidSeriesCount;
    const midpoint = Math.ceil(totalRanks / 2);

    let colorIndex;
    if (rankNumber <= midpoint) {
      // Positive ranks (1-2): Rank 1 = darkest green (last color), Rank 2 = lighter green
      colorIndex = totalRanks - rankNumber;
    } else {
      // Negative ranks (3-4): Flip the color assignment so darker reds are on the outside
      // Rank 3 (furthest from center) = darkest red, Rank 4 (closest to center) = lightest red
      // For 4-rank system: Rank 3->0 (darkest red), Rank 4->1 (lighter red)
      colorIndex = rankNumber - midpoint - 1;
    }

    const assignedColor = allColors[colorIndex] || allColors[0];
    return {
      ...series,
      color: assignedColor,
    };
  });

  // Sort the final data series to match dashboard: 8,7,6,5,4,3,2,1 (descending order)
  const finalDataSeries = coloredDataSeries.sort((a, b) => {
    const getRankNumber = (name) => {
      // Extract number from series name (e.g., "1 (HIGH)" -> 1)
      const match = name.match(/(\d+)/);
      return match ? parseInt(match[1]) : 0;
    };

    // Simple descending sort by rank number to match dashboard legend order
    return getRankNumber(b.name) - getRankNumber(a.name);
  });

  return {
    categoryLabels: reversedCategories,
    chartOptions: {
      barDir: 'bar',
      barGrouping: 'stacked',
      legendPos: 'r',
      showLegend: true,
    },
    dataSeries: finalDataSeries,
  };
};

/**
 * Creates a ranking chart slide using PptxGenJS addChart
 * @param {Object} options
 * @param {String} options.compareBy
 * @param {String} options.displayAs
 * @param {Boolean} options.groupBySegment
 * @param {Object} options.pptx
 * @param {Object} options.questionData
 * @param {String} options.questionNumber
 * @param {String} options.questionTitle
 * @param {String} options.reportType
 * @param {String} options.reportUrl
 * @param {String} options.title
 * @returns {Object}
 */
export const createRankChartSlide = ({
  compareBy,
  displayAs = 'pct',
  groupBySegment = false,
  pptx,
  questionData,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  title,
}) => {
  if (!questionTitle || questionTitle.length < 3) {
    return null;
  }

  // Check if we need to create multiple slides for different segments
  const shouldCreateMultipleSlides =
    compareBy?.value === 'quota_group' ||
    (compareBy?.value && compareBy.value !== 'overall' && questionData?.segments?.length > 1);

  if (shouldCreateMultipleSlides && questionData?.segments?.length > 1) {
    const maxSlides = Math.min(questionData.segments.length, 10);

    for (let i = 0; i < maxSlides; i++) {
      const segment = questionData.segments[i];
      const singleSegmentData = {
        ...questionData,
        segments: [segment],
      };

      const chartData = getRankChartData(singleSegmentData, displayAs);
      if (!chartData) {
        continue;
      }

      // Determine the segment name for the title
      const segmentName = segment.name || segment.id;
      const compareByLabel = compareBy?.label || compareBy?.value || compareBy;
      const slideTitle =
        compareByLabel && compareByLabel !== 'overall'
          ? `${questionTitle} - ${segmentName}`
          : `${questionTitle} - ${segmentName}`;

      createSingleRankSlide({
        chartData,
        compareBy: compareByLabel,
        displayAs,
        pptx,
        questionNumber,
        questionTitle: slideTitle,
        reportType,
        reportUrl,
        segmentName,
        title,
      });
    }
  } else {
    const chartData = getRankChartData(questionData, displayAs);
    if (!chartData) {
      return null;
    }

    createSingleRankSlide({
      chartData,
      compareBy: compareBy?.label || compareBy?.value || compareBy,
      displayAs,
      pptx,
      questionNumber,
      questionTitle,
      reportType,
      reportUrl,
      segmentName: null,
      title,
    });
  }
};

/**
 * Creates a single rank chart slide
 */
const createSingleRankSlide = ({
  chartData,
  compareBy,
  displayAs,
  pptx,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  title,
}) => {
  const cleanTitle = cleanQuestionTitle(questionTitle);
  if (!cleanTitle) {
    return null;
  }

  const slide = pptx.addSlide();

  const questionPrefix = questionNumber ? `Q${questionNumber}. ` : '';
  const titleWithQuestionNumber = `${questionPrefix}${cleanTitle}`;

  // If the questionTitle already contains segment info (like "Question - Age Group"), use it as is
  // Otherwise, add the compareBy information
  const slideTitle =
    cleanTitle.includes(' - ') || !compareBy || compareBy === 'overall'
      ? titleWithQuestionNumber
      : `${titleWithQuestionNumber} Compared by ${compareBy}`;

  const titleOptions = SURVEY_TITLE_OPTIONS;
  slide.addText(slideTitle, titleOptions);

  const chartYPosition = titleOptions.h > 1.0 ? 1.2 : 0.9;
  const chartHeight = titleOptions.h > 1.0 ? '72%' : '74%';
  const chartWidth = '98%';
  const chartXPosition = 0.05;

  try {
    const cleanedChartData = chartData.dataSeries.map((series) => ({
      ...series,
      values: series.values.map((value) => {
        if (value === null || value === undefined || isNaN(value)) {
          return 0;
        }
        const cleaned = Math.round(value * 1000) / 1000;
        if (Math.abs(cleaned) < 0.001) {
          return 0;
        }
        return cleaned;
      }),
    }));

    const isPercentageDisplay = displayAs === 'pct' || displayAs === 'percentage';

    const chartOptions = {
      barDir: 'bar',
      barGrouping: 'stacked',
      catAxisLabelFontSize: 9,
      catAxisLabelPos: 'low',
      chartColors: cleanedChartData.map((series) => series.color),
      h: chartHeight,
      legendFontSize: 10,
      legendPos: 'r',
      showCatAxisTitle: false,
      showLegend: true,
      w: chartWidth,
      x: chartXPosition,
      y: chartYPosition,
      ...(isPercentageDisplay && {
        valAxisLabelFormatCode: '0%',
        valAxisMajorUnit: 0.2,
        valAxisMaxVal: 1.0,
        valAxisMinVal: -1.0,
      }),
    };

    slide.addChart(pptx.charts.BAR, cleanedChartData, chartOptions);
  } catch (error) {
    console.error('Error creating rank chart from data:', error);
    slide.addText('Ranking chart could not be created', {
      color: TEXT_COLOR,
      fontSize: 12,
      h: 1,
      w: chartWidth,
      x: chartXPosition,
      y: chartYPosition,
    });
  }
  addSlideFooter({ pptx, reportType, reportUrl, slide });
  if (title) {
    slide.addText(
      [{ text: 'Source: ' }, { options: { hyperlink: { url: reportUrl } }, text: title }],
      FOOTER_TEXT
    );
  }

  return slide;
};

/**
 * Gets rank table data from question data
 * @param {Object} questionData
 * @param {String} displayAs
 * @param {String} segmentId
 * @returns {Object}
 */
const getRankTableData = (questionData, displayAs = 'pct', segmentId = null) => {
  const segment = segmentId
    ? questionData.segments.find((s) => s.id === segmentId)
    : questionData.segments[0];
  const isCountDisplay = displayAs === 'count';
  const rawTableData = isCountDisplay ? segment.tableCount : segment.tablePct;

  // Return the table data as-is - the backend already provides the correct headers
  return rawTableData;
};

/**
 * Creates a single rank table slide
 * @param {Object} options
 * @returns {Object}
 */
const createSingleRankTableSlide = ({
  pptx,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  slideTitle,
  tableData,
  title,
}) => {
  const slide = pptx.addSlide();
  const cleanTitle = cleanQuestionTitle(questionTitle);
  const questionPrefix = questionNumber ? `Q${questionNumber}. ` : '';
  const titleWithQuestionNumber = `${questionPrefix}${cleanTitle}`;
  const finalSlideTitle = slideTitle || titleWithQuestionNumber;

  slide.addText(finalSlideTitle, SURVEY_TITLE_OPTIONS);

  const { columns, data } = tableData;
  const maxTableRows = 16;

  // Create table rows with header
  const headerRow = columns.map((col) => ({
    options: {
      bold: true,
      color: TEXT_COLOR,
      fill: { color: COLOR_FLYWEIGHT },
      fontSize: 10,
    },
    text: col.Header || col.accessor,
  }));

  const dataRows = data.slice(0, maxTableRows).map((row) => {
    return columns.map((col) => {
      const value = row[col.accessor];
      let formattedValue = value;

      if (col.format && typeof value === 'number') {
        formattedValue = formatData({ format: col.format, value });
      }

      return {
        options: {
          color: TEXT_COLOR,
          fontSize: 9,
        },
        text: formattedValue?.toString() || '',
      };
    });
  });

  const tableRows = [headerRow, ...dataRows];

  // Calculate table dimensions
  const tableWidth = 9;
  const tableHeight = Math.min(4.5, tableRows.length * 0.25 + 0.5);
  const tableX = 0.5;
  const tableY = 1.5;

  try {
    slide.addTable(tableRows, {
      border: TABLE_BORDER,
      colW: Array(columns.length).fill(tableWidth / columns.length),
      h: tableHeight,
      w: tableWidth,
      x: tableX,
      y: tableY,
    });
  } catch (error) {
    console.error('Error creating rank table:', error);
    slide.addText('Ranking table could not be created', {
      color: TEXT_COLOR,
      fontSize: 12,
      h: 1,
      w: tableWidth,
      x: tableX,
      y: tableY,
    });
  }

  addSlideFooter({ pptx, reportType, reportUrl, slide });
  if (title) {
    slide.addText(
      [{ text: 'Source: ' }, { options: { hyperlink: { url: reportUrl } }, text: title }],
      FOOTER_TEXT
    );
  }

  return slide;
};

/**
 * Creates rank table slides
 * @param {Object} options
 * @param {String} options.compareBy
 * @param {String} options.displayAs
 * @param {Object} options.pptx
 * @param {Object} options.questionData
 * @param {Number} options.questionNumber
 * @param {String} options.questionTitle
 * @param {String} options.reportType
 * @param {String} options.reportUrl
 * @param {String} options.title
 * @returns {Object}
 */
export const createRankTableSlide = ({
  compareBy,
  displayAs = 'pct',
  pptx,
  questionData,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  title,
}) => {
  const cleanTitle = cleanQuestionTitle(questionTitle);
  const questionPrefix = questionNumber ? `Q${questionNumber}. ` : '';
  const baseSlideTitle = `${questionPrefix}${cleanTitle}`;

  // Check if we need to create multiple slides for different segments
  if (questionData?.segments?.length > 1) {
    // Create slides for each segment
    const maxSlides = Math.min(questionData.segments.length, 10);
    for (let i = 0; i < maxSlides; i++) {
      const segment = questionData.segments[i];
      const tableData = getRankTableData(questionData, displayAs, segment.id);

      if (!tableData) {
        continue;
      }

      // Handle splitting for rank tables with >16 rows (matching other slide logic)
      const maxRowsPerSlide = 16;
      if (tableData.data.length > maxRowsPerSlide) {
        let slideIndex = 1;
        for (let startRow = 0; startRow < tableData.data.length; startRow += maxRowsPerSlide) {
          const endRow = Math.min(startRow + maxRowsPerSlide, tableData.data.length);
          const splitData = {
            ...tableData,
            data: tableData.data.slice(startRow, endRow),
          };

          const slideTitle =
            tableData.data.length > maxRowsPerSlide
              ? `${baseSlideTitle} - ${segment.name} (${slideIndex})`
              : `${baseSlideTitle} - ${segment.name}`;

          createSingleRankTableSlide({
            pptx,
            questionNumber,
            questionTitle,
            reportType,
            reportUrl,
            slideTitle,
            tableData: splitData,
            title,
          });

          slideIndex++;
        }
      } else {
        createSingleRankTableSlide({
          pptx,
          questionNumber,
          questionTitle,
          reportType,
          reportUrl,
          slideTitle: `${baseSlideTitle} - ${segment.name}`,
          tableData: tableData,
          title,
        });
      }
    }
  } else {
    // Single segment case
    const tableData = getRankTableData(questionData, displayAs);

    if (!tableData) {
      return null;
    }

    // Handle splitting for rank tables with >16 rows (matching other slide logic)
    const maxRowsPerSlide = 16;
    if (tableData.data.length > maxRowsPerSlide) {
      let slideIndex = 1;
      for (let startRow = 0; startRow < tableData.data.length; startRow += maxRowsPerSlide) {
        const endRow = Math.min(startRow + maxRowsPerSlide, tableData.data.length);
        const splitData = {
          ...tableData,
          data: tableData.data.slice(startRow, endRow),
        };

        const slideTitle =
          tableData.data.length > maxRowsPerSlide
            ? `${baseSlideTitle} (${slideIndex})`
            : baseSlideTitle;

        createSingleRankTableSlide({
          pptx,
          questionNumber,
          questionTitle,
          reportType,
          reportUrl,
          slideTitle,
          tableData: splitData,
          title,
        });

        slideIndex++;
      }
    } else {
      createSingleRankTableSlide({
        pptx,
        questionNumber,
        questionTitle,
        reportType,
        reportUrl,
        slideTitle: baseSlideTitle,
        tableData: tableData,
        title,
      });
    }
  }

  return null;
};
